import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Floating custom cursor:
 *  - small gold dot at exact pointer position (no lag)
 *  - larger ring lagging behind with spring physics (the "floating" feel)
 *  - ring scales + glows over interactive elements (a, button, [role=button], input)
 *  - only active on hover-capable, fine-pointer devices (skips touch)
 */
export const CustomCursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [hidden, setHidden] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 180, damping: 22, mass: 0.6 });
  const ry = useSpring(y, { stiffness: 180, damping: 22, mass: 0.6 });

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!mq.matches) return;
    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");

    const onMove = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      const t = e.target;
      if (!(t instanceof Element)) return;
      setInteractive(!!t.closest('a, button, [role="button"], input, textarea, select, [data-magnetic]'));
    };
    const onLeave = () => setHidden(true);
    const onEnter = () => setHidden(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerleave", onLeave);
    window.addEventListener("pointerenter", onEnter);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("pointerenter", onEnter);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <>
      {/* Trailing ring — spring physics create the floating lag */}
      <motion.div
        data-testid="custom-cursor-ring"
        style={{ x: rx, y: ry, opacity: hidden ? 0 : 1 }}
        className="pointer-events-none fixed top-0 left-0 z-[130] -translate-x-1/2 -translate-y-1/2"
      >
        <motion.div
          animate={{
            scale: interactive ? 1.9 : 1,
            borderColor: interactive ? "rgba(220,194,134,0.9)" : "rgba(197,160,89,0.55)",
          }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="w-8 h-8 rounded-full border shadow-[0_0_18px_rgba(197,160,89,0.35)]"
        />
      </motion.div>

      {/* Exact-position dot */}
      <motion.div
        data-testid="custom-cursor-dot"
        style={{ x, y, opacity: hidden ? 0 : 1 }}
        className="pointer-events-none fixed top-0 left-0 z-[131] -translate-x-1/2 -translate-y-1/2"
      >
        <motion.div
          animate={{ scale: interactive ? 0 : 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className="w-1.5 h-1.5 rounded-full bg-[#c5a059]"
        />
      </motion.div>
    </>
  );
};
